import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ZodNumber, ZodString } from "zod";
import {
  annualDateTimeframeSchema,
  fullDateTimeframeSchema,
} from "../marketing/segments/segment-timeframe-zod-schema";
import {
  ComparisonType,
  ExistsCompareOperators,
  ListCompareOperators,
  NumericCompareOperator,
  ProximityCompareOperators,
  ProximityUnit,
  StringCompareOperators,
  TokenCompareOperators,
  WhereConditionDataType,
} from "./segment-where-condition";
const listCompareSchema = <T extends ZodNumber | ZodString>(zType: T) =>
  z.object({
    type: z.literal(ComparisonType.LIST),
    operator: createEnum(ListCompareOperators, {
      enumName: "ListCompareOperators",
    }),
    values: z.array(zType),
  });

const tokenCompareSchema = z
  .object({
    type: z.literal(ComparisonType.TOKEN),
    operator: createEnum(TokenCompareOperators, {
      enumName: "TokenCompareOperators",
    }),
    values: z.array(z.string()),
  })
  .meta({ id: "tokenCompare" });

const tokenConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.TOKEN),
    dimension,
    comparison: tokenCompareSchema,
  });

const numericCompareSchema = z
  .object({
    type: z.literal(ComparisonType.NUMERIC),
    operator: createEnum(NumericCompareOperator, {
      enumName: "NumericCompareOperator",
    }),
    value: z.number(),
  })
  .meta({ id: "numericCompare" });

const numericConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.NUMERIC),
    dimension,
    comparison: numericCompareSchema,
  });

const booleanCompareSchema = z
  .object({ type: z.literal(ComparisonType.BOOLEAN), value: z.boolean() })
  .meta({ id: "booleanCompare" });

const existsCompareSchema = z
  .object({
    type: z.literal(ComparisonType.EXISTS),
    operator: createEnum(ExistsCompareOperators, {
      enumName: "ExistsCompareOperators",
    }),
  })
  .meta({ id: "existsCompare" });

const booleanConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.BOOLEAN),
    dimension,
    comparison: booleanCompareSchema,
  });

const fullDateConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.DATE_FULL),
    dimension,
    comparison: fullDateTimeframeSchema,
  });

const annualDateConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.DATE_ANNUAL),
    dimension,
    comparison: annualDateTimeframeSchema as z.ZodTypeAny,
  });

const tokenListConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.TOKEN_LIST),
    dimension,
    comparison: listCompareSchema(z.string()),
  });

const tokenHierarchyCompareSchema = z
  .object({
    type: z.literal(ComparisonType.TOKEN),
    operator: createEnum(TokenCompareOperators, {
      enumName: "TokenCompareOperators",
    }),
    /**
     * Prerequisite values that must be true before comparing against the leaf node values
     * e.g., ["US"] for TokenHierarchyType.STATE_REGION
     */
    prerequisiteValues: z.array(z.string()),
    /**
     * The leaf node values to compare against, e.g., ["provo", "orem"] but populated dynamically based on prerequisites
     */
    values: z.array(z.string()),
  })
  .meta({ id: "tokenHierarchyCompare" });

const tokenHierarchyConditionSchema = <D extends z.ZodType<string>>(
  dimension: D,
) =>
  z.object({
    type: z.literal(WhereConditionDataType.TOKEN_HIERARCHY),
    dimension,
    comparison: tokenHierarchyCompareSchema,
  });

const cityProximityCompareSchema = z
  .object({
    type: z.literal(ComparisonType.PROXIMITY),
    operator: createEnum(ProximityCompareOperators, {
      enumName: "ProximityCompareOperators",
    }),
    prerequisiteValues: z.array(z.string()), // [country, state]
    value: z.string(), // city
    options: z.object({
      radius: z.number(),
      units: createEnum(ProximityUnit, { enumName: "ProximityUnit" }),
    }),
  })
  .meta({ id: "cityProximityCompare" });

const cityProximityConditionSchema = <D extends z.ZodType<string>>(
  dimension: D,
) =>
  z.object({
    type: z.literal(WhereConditionDataType.CITY_PROXIMITY),
    dimension,
    comparison: cityProximityCompareSchema,
  });

const stringCompareSchema = z
  .object({
    type: z.literal(ComparisonType.STRING),
    operator: createEnum(StringCompareOperators, {
      enumName: "StringCompareOperators",
    }),
    value: z.string(),
  })
  .meta({ id: "stringCompare" });

const stringConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.STRING),
    dimension,
    comparison: stringCompareSchema,
  });

const customFieldConditionSchema = <D extends z.ZodType<string>>(
  dimension: D,
) =>
  z.object({
    type: z.literal(WhereConditionDataType.CUSTOM_FIELD),
    dimension,
    declarationId: z.string(),
    comparison: z.union([
      tokenCompareSchema,
      numericCompareSchema,
      booleanCompareSchema,
      existsCompareSchema,
      fullDateTimeframeSchema,
    ]),
  });

/**
 * Every condition shape, over whatever vocabulary of dimensions the caller
 * has. Checkout segments and marketing segments share these shapes but not
 * their dimensions, so the dimension is a parameter rather than an open string.
 */
const conditionSchemaByDataType = {
  [WhereConditionDataType.TOKEN]: tokenConditionSchema,
  [WhereConditionDataType.NUMERIC]: numericConditionSchema,
  [WhereConditionDataType.BOOLEAN]: booleanConditionSchema,
  [WhereConditionDataType.DATE_FULL]: fullDateConditionSchema,
  [WhereConditionDataType.DATE_ANNUAL]: annualDateConditionSchema,
  [WhereConditionDataType.TOKEN_LIST]: tokenListConditionSchema,
  [WhereConditionDataType.TOKEN_HIERARCHY]: tokenHierarchyConditionSchema,
  [WhereConditionDataType.CITY_PROXIMITY]: cityProximityConditionSchema,
  [WhereConditionDataType.CUSTOM_FIELD]: customFieldConditionSchema,
  [WhereConditionDataType.STRING]: stringConditionSchema,
};

type ConditionBranch = ReturnType<
  (typeof conditionSchemaByDataType)[WhereConditionDataType]
>;

/**
 * Only the branches the caller has dimensions for. A vocabulary that offers no
 * date dimensions should not advertise a date condition.
 */
export function whereConditionSchemaForDimensions(
  dimensionsByDataType: Partial<
    Record<WhereConditionDataType, readonly [string, ...string[]]>
  >,
) {
  const branches = Object.entries(dimensionsByDataType).flatMap(
    ([dataType, dimensions]) =>
      dimensions
        ? [
            conditionSchemaByDataType[dataType as WhereConditionDataType](
              z.enum(dimensions as [string, ...string[]]),
            ),
          ]
        : [],
  );
  return z.discriminatedUnion(
    "type",
    branches as unknown as [
      ConditionBranch,
      ConditionBranch,
      ...ConditionBranch[],
    ],
  );
}

export function whereConditionSchemaFor<D extends z.ZodType<string>>(
  dimension: D,
) {
  return z.discriminatedUnion("type", [
    tokenConditionSchema(dimension),
    numericConditionSchema(dimension),
    booleanConditionSchema(dimension),
    fullDateConditionSchema(dimension),
    annualDateConditionSchema(dimension),
    tokenListConditionSchema(dimension),
    tokenHierarchyConditionSchema(dimension),
    cityProximityConditionSchema(dimension),
    customFieldConditionSchema(dimension),
    stringConditionSchema(dimension),
  ]);
}

export const whereConditionSchema = whereConditionSchemaFor(z.string()).meta({
  id: "whereCondition",
});

/**
 * Narrows each branch's dimension without restating the branch. Checkout
 * segments name a single dimension this way; marketing leaves it open.
 */
type WithDimension<T, D extends string> = T extends unknown
  ? { [K in keyof T]: K extends "dimension" ? D : T[K] }
  : never;

export type WhereCondition<D extends string = string> = WithDimension<
  z.infer<typeof whereConditionSchema>,
  D
>;

export type TokenCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.TOKEN }
>;

export type NumericCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.NUMERIC }
>;

export type BooleanCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.BOOLEAN }
>;

export type FullDateCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.DATE_FULL }
>;

export type AnnualDateCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.DATE_ANNUAL }
>;

export type TokenListCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.TOKEN_LIST }
>;

export type TokenHierarchyCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.TOKEN_HIERARCHY }
>;

export type CityProximityCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.CITY_PROXIMITY }
>;

export type CustomFieldCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.CUSTOM_FIELD }
>;

export type StringCondition<D extends string = string> = Extract<
  WhereCondition<D>,
  { type: WhereConditionDataType.STRING }
>;

export type TokenCompare = z.infer<typeof tokenCompareSchema>;
export type NumericCompare = z.infer<typeof numericCompareSchema>;
export type BooleanCompare = z.infer<typeof booleanCompareSchema>;
export type ExistsCompare = z.infer<typeof existsCompareSchema>;
export type StringCompare = z.infer<typeof stringCompareSchema>;
export type TokenHierarchyCompare = z.infer<typeof tokenHierarchyCompareSchema>;
export type CityProximityCompare = z.infer<typeof cityProximityCompareSchema>;
export type ListCompare = TokenListCondition["comparison"];
